import React, { createContext, useContext, useState } from 'react'
import { AgTechAppContext, DefaultAgTechAppContext, IAgTechAppContext } from '@agtech/core/app/AgTechAppContext';
import { AgTechApplication } from '@agtech/core/app/AgTechApplication';
import AgTechToastSurface, { AgTechToastManager, DefaultAgTechToastManager, useToasts as useAgTechWebToasts } from '../Functions/AgTechWebToasts';
import AgTechModalSurface, { AgTechModalManager, DefaultAgTechModalManager, useModals as useAgTechWebModals } from '../Functions/AgTechModals';
import AgTechDialogSurface, { AgTechDialogManager, DefaultAgTechDialogManager, useDialogs as useAgTechWebDialogs } from '../Functions/AgTechDialogs';
import AgTechPopUpSurface, { AgTechPopUpManager, AgTechPopUpRenderProps, DefaultAgTechPopUpManager, usePopups as useAgTechWebPopUps } from '../Functions/AgTechPopUps';
import { AgTechNavigationManager, DefaultAgTechNavigationManager, useAgTechWebNavigation } from '../Functions/AgTechNavigation';
import { useAgTechWebDataLoader } from '../Functions/AgTechWebLoaders';
import { AgTechRenderedPopUp, useAgTechPopUpContext } from '@agtech/core/functions/AgTechAppPopUps';

/// Internal

type AgTechInternalWebAppContext = IAgTechAppContext & {
    toasts: AgTechToastManager,
    modals: AgTechModalManager,
    dialogs: AgTechDialogManager,
    popups: AgTechPopUpManager,
    navigation: AgTechNavigationManager,
};

const WebAppContext = createContext<AgTechInternalWebAppContext>({
    ...DefaultAgTechAppContext,
    toasts: DefaultAgTechToastManager,
    modals: DefaultAgTechModalManager,
    dialogs: DefaultAgTechDialogManager,
    popups: DefaultAgTechPopUpManager,
    navigation: DefaultAgTechNavigationManager,
});

export type AgTechAppContextProps = {
    app: AgTechApplication
}

let AgTechWebAppContextInstance = (props: React.PropsWithChildren<AgTechAppContextProps>) => {
    let toastManager = useAgTechWebToasts();
    let modalManager = useAgTechWebModals();
    let dialogManager = useAgTechWebDialogs();
    let popupManager = useAgTechWebPopUps();
    let navigationManager = useAgTechWebNavigation();
    let loadingManager = useAgTechWebDataLoader();

    let [appContext, updateAppContext] = useState<AgTechInternalWebAppContext>({
        toasts: toastManager,
        modals: modalManager,
        dialogs: dialogManager,
        popups: popupManager,
        navigation: navigationManager,
        messaging: toastManager,
        confirmations: modalManager,
        loading: loadingManager,
    });

    return (
        <div className="stretched fading-in flex">
            <div className="relative stretched container" id="agtech-app">
                <AgTechAppContext.Provider value={appContext}>
                    <WebAppContext.Provider value={appContext}>
                        {props.children}
                        <AgTechModalSurface modalManager={modalManager} />
                        <AgTechDialogSurface dialogManager={dialogManager} />
                        <AgTechPopUpSurface popupManager={popupManager} />
                        <AgTechToastSurface toastManager={toastManager} />
                    </WebAppContext.Provider>
                </AgTechAppContext.Provider>
            </div>
        </div>
    )
};

/// External

export type AgTechExternalPopUpManager = {
    showPopUp: (popup: AgTechPopUpRenderProps) => AgTechRenderedPopUp,
    closeAll: () => void
}

export type AgTechWebAppContext = IAgTechAppContext & {
    toasts: AgTechToastManager,
    modals: AgTechModalManager,
    dialogs: AgTechDialogManager,
    popups: AgTechExternalPopUpManager,
    navigation: AgTechNavigationManager,
};

export const useAgTechWebAppContext = (): AgTechWebAppContext => {
    const appContext = useContext(WebAppContext);
    const popupContext = useAgTechPopUpContext();

    return {
        ...appContext,
        popups: {
            ...appContext.popups,
            showPopUp: (props: AgTechPopUpRenderProps) => {
                const renderedPopup = appContext.popups.showPopUp({
                    ...props,
                    parentPopUpContext: popupContext
                });

                return renderedPopup;
            }
        }
    }
};

export default React.memo(AgTechWebAppContextInstance);